import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Calendar, ChevronLeft, ChevronRight, Clock, Loader2 } from 'lucide-react';
import SimpleTooltip from './SimpleTooltip';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Schedule an approved Deep Onboarding session.
 * Same calendar as before but only shown after admin approval.
 */
export default function DeepScheduleModal({ isOpen, onClose, sessionId, onSuccess }) {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [slots, setSlots] = useState([]);
  const [dateError, setDateError] = useState('');
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const token = localStorage.getItem('auth_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadSlots = useCallback(async (dateStr) => {
    setSlotsLoading(true); setDateError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/assisted-onboarding/available-slots?date_str=${dateStr}&session_type=deep_onboarding`, { headers, cache: 'no-store' });
      const data = await res.json();
      if (!data.available) { setDateError(data.reason); setSlots([]); }
      else { setSlots(data.slots); }
    } catch (err) { logger.error(err); setDateError('Failed to load slots'); }
    finally { setSlotsLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateClick = (dateStr) => {
    setSelectedDate(dateStr); setSelectedTime(''); loadSlots(dateStr);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/assisted-onboarding/deep/${sessionId}/schedule`, {
        method: 'POST', headers,
        body: JSON.stringify({ scheduled_date: selectedDate, scheduled_time: selectedTime })
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      onSuccess?.();
      onClose();
    } catch (err) { alert(err.message); }
    finally { setSubmitting(false); }
  };

  const isDateDisabled = (d) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const daysUntilNextMon = (7 - today.getDay()) % 7 || 7;
    const nextMonday = new Date(today); nextMonday.setDate(today.getDate() + daysUntilNextMon);
    if (d < nextMonday) return true;
    if (d.getDay() === 0 || d.getDay() === 6) return true;
    const maxDate = new Date(today); maxDate.setDate(today.getDate() + 60);
    if (d > maxDate) return true;
    const holidays = new Set(['1-1','1-2','5-1','6-12','10-1','12-25','12-26','12-31','7-4','11-11']);
    return holidays.has(`${d.getMonth()+1}-${d.getDate()}`);
  };

  const renderCalendar = () => {
    const year = calendarMonth.getFullYear(); const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    while (days.length % 7 !== 0) days.push(null);
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <SimpleTooltip content="Previous month">
            <Button variant="ghost" size="icon" aria-label="Previous month" onClick={() => setCalendarMonth(new Date(year, month - 1, 1))}><ChevronLeft className="w-4 h-4" /></Button>
          </SimpleTooltip>
          <span className="font-semibold text-sm">{monthNames[month]} {year}</span>
          <SimpleTooltip content="Next month">
            <Button variant="ghost" size="icon" aria-label="Next month" onClick={() => setCalendarMonth(new Date(year, month + 1, 1))}><ChevronRight className="w-4 h-4" /></Button>
          </SimpleTooltip>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-xs mb-1">
          {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <div key={d} className="text-slate-400 font-medium py-1">{d}</div>)}
        </div>
        {weeks.map((week, wi) => (
          <div key={`week-${wi}`} className="grid grid-cols-7 gap-1">
            {week.map((day, di) => {
              if (!day) return <div key={`day-${di}`} />;
              const dateObj = new Date(year, month, day);
              const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const disabled = isDateDisabled(dateObj);
              const isSelected = dateStr === selectedDate;
              return (
                <button key={`day-${di}`} disabled={disabled} onClick={() => !disabled && handleDateClick(dateStr)}
                  className={`w-8 h-8 rounded-md text-xs font-medium transition-colors
                    ${disabled ? 'text-slate-200 cursor-not-allowed' : 'hover:bg-emerald-50 cursor-pointer text-slate-700'}
                    ${isSelected ? 'bg-emerald-600 text-white hover:bg-emerald-700' : ''}`}>
                  {day}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="deep-schedule-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-emerald-600" />
            Schedule Your Deep Onboarding
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500 mt-1">Your request has been approved. Pick a date and time for your 60-minute session.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {renderCalendar()}
          {dateError && <p className="text-xs text-red-500">{dateError}</p>}
          {selectedDate && slots.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-600 mb-2"><Clock className="w-3 h-3 inline mr-1" />Available slots</p>
              <div className="grid grid-cols-4 gap-2">
                {slots.map(s => (
                  <button key={s.time} onClick={() => setSelectedTime(s.time)}
                    className={`text-xs py-2 px-1 rounded-md border transition-colors
                      ${selectedTime === s.time ? 'bg-emerald-600 text-white border-emerald-600' : 'border-slate-200 hover:border-emerald-400 text-slate-600'}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={handleSubmit} disabled={!selectedDate || !selectedTime || submitting}
            className="bg-emerald-600 hover:bg-emerald-700" data-testid="deep-schedule-submit">
            {submitting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            Confirm Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
